import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LogOut, Users, FileText, BarChart3, Eye, Send, Download, 
  ChevronRight, Clock, CheckCircle, AlertCircle, Briefcase, 
  GraduationCap, Building2, RefreshCw, Search, Sparkles, Languages
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#CC5500', '#C5A065', '#4A90A4', '#50C878'];

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!user.is_admin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, reportsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/reports?per_page=50`),
        axios.get(`${API}/admin/users?per_page=50`),
      ]);
      setStats(statsRes.data);
      setReports(reportsRes.data.reports || []);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/admin/report/${reportId}`);
      setSelectedReport(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    }
  };

  const handleReleaseReport = async (reportId) => {
    try {
      await axios.post(`${API}/admin/report/${reportId}/release`);
      toast.success('Report released to user');
      fetchData();
      setSelectedReport(null);
    } catch (error) {
      toast.error('Failed to release report');
    }
  };

  const handleDownloadReport = async (reportId, format = 'pdf') => {
    try {
      // Use admin-specific endpoint for downloads
      const response = await axios.get(`${API}/admin/report/download/${reportId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `LeadEconomics_Report_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      generated: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'Pending Review' },
      viewed_by_admin: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Reviewed' },
      sent_to_user: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Released' },
    };
    const config = statusConfig[status] || statusConfig.generated;
    return <Badge className={`${config.color} border`}>{config.label}</Badge>;
  };

  const getUserTypeIcon = (type) => {
    const icons = {
      individual: Briefcase,
      student: GraduationCap,
      corporate: Building2,
    };
    const Icon = icons[type] || Briefcase;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-[#CC5500]/20 rounded-full" />
            <div className="absolute inset-0 border-4 border-transparent border-t-[#CC5500] rounded-full animate-spin" />
          </div>
          <p className="text-gray-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const userTypeData = stats ? [
    { name: 'Professional', value: stats.user_breakdown?.individual || 0 },
    { name: 'Student', value: stats.user_breakdown?.student || 0 },
    { name: 'Corporate', value: stats.user_breakdown?.corporate || 0 },
  ] : [];

  const filteredReports = reports.filter(r => 
    r.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="admin-dashboard">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#0D0D0D]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-heading font-semibold text-white">
                Lead<span className="text-[#CC5500]">Economics</span>™
              </h1>
              <Badge className="bg-[#CC5500]/20 text-[#CC5500] border border-[#CC5500]/30">
                Admin Portal
              </Badge>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin/translation-test')}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:opacity-90 text-white"
              >
                <Languages className="h-4 w-4 mr-2" />
                Translation Test
              </Button>
              <Button
                onClick={() => navigate('/admin/compatibility')}
                className="bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:opacity-90 text-white"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Compatibility Report
              </Button>
              <Button
                onClick={fetchData}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="h-5 w-5 text-[#CC5500]" />
              <span className="text-xs text-gray-500">Total</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats?.total_users || 0}</p>
            <p className="text-sm text-gray-400">Registered Users</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <FileText className="h-5 w-5 text-[#C5A065]" />
              <span className="text-xs text-gray-500">Total</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats?.total_assessments || 0}</p>
            <p className="text-sm text-gray-400">Assessments</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="h-5 w-5 text-[#4A90A4]" />
              <span className="text-xs text-gray-500">Generated</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats?.total_reports || 0}</p>
            <p className="text-sm text-gray-400">Reports</p>
          </div>
          <div className="bg-gradient-to-br from-[#CC5500]/20 to-transparent border border-[#CC5500]/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <AlertCircle className="h-5 w-5 text-[#CC5500]" />
              <span className="text-xs text-[#CC5500]">Action Required</span>
            </div>
            <p className="text-3xl font-bold text-[#CC5500]">{stats?.pending_reports || 0}</p>
            <p className="text-sm text-gray-400">Pending Review</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#CC5500] data-[state=active]:to-[#C5A065] data-[state=active]:text-white rounded-lg">
              Overview
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#CC5500] data-[state=active]:to-[#C5A065] data-[state=active]:text-white rounded-lg">
              Reports ({reports.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#CC5500] data-[state=active]:to-[#C5A065] data-[state=active]:text-white rounded-lg">
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="demo" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-600 data-[state=active]:to-green-500 data-[state=active]:text-white rounded-lg">
              🧪 Demo Testing
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* User Type Distribution */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">User Distribution</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={userTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {userTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Sample Reports Section */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Sample Reports (Download)</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Briefcase className="h-5 w-5 text-[#CC5500]" />
                      <div>
                        <p className="text-sm text-white font-medium">Professional - Comprehensive</p>
                        <p className="text-xs text-gray-500">40 pages | Leadership Personality Report</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => window.open(`${API}/reports/sample/professional?tier=comprehensive`, '_blank')}
                      className="bg-[#CC5500] hover:bg-[#CC5500]/90"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <GraduationCap className="h-5 w-5 text-[#C5A065]" />
                      <div>
                        <p className="text-sm text-white font-medium">Student - Comprehensive</p>
                        <p className="text-xs text-gray-500">10 pages | Career Compatibility Report</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive`, '_blank')}
                      className="bg-[#C5A065] hover:bg-[#C5A065]/90"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-[#4A90A4]" />
                      <div>
                        <p className="text-sm text-white font-medium">Compatibility Report</p>
                        <p className="text-xs text-gray-500">9 pages | 360° Analysis</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => window.open(`${API}/reports/sample/compatibility`, '_blank')}
                      className="bg-[#4A90A4] hover:bg-[#4A90A4]/90"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                  
                  {/* Other tiers */}
                  <div className="pt-3 border-t border-white/10">
                    <p className="text-xs text-gray-500 mb-2">All Report Tiers (with Language Support):</p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="border-[#CC5500]/50 text-[#CC5500] text-xs" onClick={() => window.open(`${API}/reports/sample/professional?tier=express`, '_blank')}>
                        Pro Express (2pg)
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#CC5500]/50 text-[#CC5500] text-xs" onClick={() => window.open(`${API}/reports/sample/professional?tier=basic`, '_blank')}>
                        Pro Basic (10pg)
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#CC5500]/50 text-[#CC5500] text-xs" onClick={() => window.open(`${API}/reports/sample/professional?tier=detailed`, '_blank')}>
                        Pro Detailed (18pg)
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#C5A065]/50 text-[#C5A065] text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=express`, '_blank')}>
                        Student Express (3pg)
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#C5A065]/50 text-[#C5A065] text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=basic`, '_blank')}>
                        Student Basic (5pg)
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#C5A065]/50 text-[#C5A065] text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=detailed`, '_blank')}>
                        Student Detailed (14pg)
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-3 mb-2">Indian Languages:</p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="border-white/20 text-white text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=hi`, '_blank')}>
                        Hindi (हिंदी)
                      </Button>
                      <Button size="sm" variant="outline" className="border-white/20 text-white text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=mr`, '_blank')}>
                        Marathi (मराठी)
                      </Button>
                      <Button size="sm" variant="outline" className="border-white/20 text-white text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=ta`, '_blank')}>
                        Tamil (தமிழ்)
                      </Button>
                      <Button size="sm" variant="outline" className="border-white/20 text-white text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=te`, '_blank')}>
                        Telugu (తెలుగు)
                      </Button>
                      <Button size="sm" variant="outline" className="border-white/20 text-white text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=bn`, '_blank')}>
                        Bengali (বাংলা)
                      </Button>
                      <Button size="sm" variant="outline" className="border-white/20 text-white text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=gu`, '_blank')}>
                        Gujarati (ગુજરાતી)
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-3 mb-2">International Languages:</p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="border-blue-500/30 text-blue-400 text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=es`, '_blank')}>
                        Spanish (Español)
                      </Button>
                      <Button size="sm" variant="outline" className="border-blue-500/30 text-blue-400 text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=fr`, '_blank')}>
                        French (Français)
                      </Button>
                      <Button size="sm" variant="outline" className="border-blue-500/30 text-blue-400 text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=de`, '_blank')}>
                        German (Deutsch)
                      </Button>
                      <Button size="sm" variant="outline" className="border-blue-500/30 text-blue-400 text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=ar`, '_blank')}>
                        Arabic (العربية)
                      </Button>
                      <Button size="sm" variant="outline" className="border-blue-500/30 text-blue-400 text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=zh`, '_blank')}>
                        Chinese (中文)
                      </Button>
                      <Button size="sm" variant="outline" className="border-blue-500/30 text-blue-400 text-xs" onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive&language=ja`, '_blank')}>
                        Japanese (日本語)
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Pending Reports */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Recent User Reports</h3>
              <div className="space-y-3">
                {reports.filter(r => r.status === 'generated').slice(0, 5).map((report) => (
                  <div key={report.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      {getUserTypeIcon(report.user_type)}
                      <div>
                        <p className="text-sm text-white font-medium">{report.user_name}</p>
                        <p className="text-xs text-gray-500">{report.user_email}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleViewReport(report.id)}
                      className="bg-[#CC5500] hover:bg-[#CC5500]/90"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Review
                    </Button>
                  </div>
                ))}
                {reports.filter(r => r.status === 'generated').length === 0 && (
                  <p className="text-gray-500 text-center py-4">No pending user reports. Download sample reports above to preview.</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              {/* Search */}
              <div className="flex items-center gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>

              {/* Reports Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Tier</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Assessment</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReports.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-12 text-center text-gray-500">
                          No reports found {searchQuery && 'matching your search'}
                        </td>
                      </tr>
                    ) : filteredReports.map((report) => (
                      <tr key={report.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            {getUserTypeIcon(report.user_type)}
                            <div>
                              <p className="text-sm text-white font-medium">{report.user_name}</p>
                              <p className="text-xs text-gray-500">{report.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm text-gray-400 capitalize">{report.user_type}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                            report.report_tier === 'comprehensive' ? 'bg-purple-500/20 text-purple-400' :
                            report.report_tier === 'detailed' ? 'bg-blue-500/20 text-blue-400' :
                            report.report_tier === 'basic' ? 'bg-green-500/20 text-green-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {report.report_tier || 'N/A'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm text-gray-400 capitalize">{report.assessment_type}</span>
                        </td>
                        <td className="py-4 px-4">
                          {getStatusBadge(report.status)}
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm text-gray-400">
                            {new Date(report.created_at).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewReport(report.id)}
                              className="border-white/20 text-white hover:bg-white/10"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadReport(report.id, 'pdf')}
                              className="border-white/20 text-white hover:bg-white/10"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {report.status !== 'sent_to_user' && (
                              <Button
                                size="sm"
                                onClick={() => handleReleaseReport(report.id)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Release
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Organization</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            {getUserTypeIcon(u.user_type)}
                            <div>
                              <p className="text-sm text-white font-medium">{u.full_name}</p>
                              <p className="text-xs text-gray-500">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className="bg-white/10 text-gray-300 border-white/20 capitalize">
                            {u.user_type}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm text-gray-400">{u.organization || '-'}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm text-gray-400">
                            {new Date(u.created_at).toLocaleDateString()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Demo Testing Tab */}
          <TabsContent value="demo">
            <div className="space-y-6">
              {/* Demo Mode Instructions */}
              <div className="bg-gradient-to-r from-green-500/10 to-green-600/5 border border-green-500/30 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-green-400 mb-2">🧪 Demo Testing Mode</h3>
                <p className="text-gray-400 text-sm">
                  Use this section to test the complete assessment flow without payment. 
                  Create demo users or test as admin to experience the full journey.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Quick Test as Admin */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-[#CC5500]" />
                    Test as Professional
                  </h4>
                  <p className="text-gray-400 text-sm mb-4">
                    Bypass payment and test the complete professional assessment flow.
                  </p>
                  <Button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/demo/bypass-payment`, { tier: 'comprehensive' });
                        toast.success('Demo mode activated! Redirecting...');
                        setTimeout(() => window.location.href = '/instructions', 1000);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed');
                      }
                    }}
                    className="w-full bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:opacity-90"
                  >
                    Start Professional Assessment (No Payment)
                  </Button>
                </div>

                {/* Create Demo Student */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-[#C5A065]" />
                    Create Demo Student Account
                  </h4>
                  <p className="text-gray-400 text-sm mb-4">
                    Create a test student account with payment bypassed.
                  </p>
                  <Button
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/demo/create-test-user`, { 
                          user_type: 'student', 
                          tier: 'comprehensive' 
                        });
                        toast.success(`Demo student created!\nEmail: ${res.data.credentials.email}\nPassword: ${res.data.credentials.password}`, {
                          duration: 10000
                        });
                        fetchData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed');
                      }
                    }}
                    className="w-full bg-gradient-to-r from-[#C5A065] to-[#CC5500] hover:opacity-90"
                  >
                    Create Demo Student
                  </Button>
                </div>

                {/* Create Demo Professional */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-[#4A90A4]" />
                    Create Demo Professional Account
                  </h4>
                  <p className="text-gray-400 text-sm mb-4">
                    Create a test professional account with payment bypassed.
                  </p>
                  <Button
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/demo/create-test-user`, { 
                          user_type: 'individual', 
                          tier: 'comprehensive' 
                        });
                        toast.success(`Demo professional created!\nEmail: ${res.data.credentials.email}\nPassword: ${res.data.credentials.password}`, {
                          duration: 10000
                        });
                        fetchData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed');
                      }
                    }}
                    className="w-full bg-gradient-to-r from-[#4A90A4] to-[#50C878] hover:opacity-90"
                  >
                    Create Demo Professional
                  </Button>
                </div>

                {/* Download Sample Reports */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#50C878]" />
                    Download Sample Reports
                  </h4>
                  <p className="text-gray-400 text-sm mb-4">
                    Preview comprehensive sample reports to show clients.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => window.open(`${API}/reports/sample/professional?tier=comprehensive`, '_blank')}
                      variant="outline"
                      className="flex-1 border-[#CC5500] text-[#CC5500] hover:bg-[#CC5500]/10"
                    >
                      Professional (40pg)
                    </Button>
                    <Button
                      onClick={() => window.open(`${API}/reports/sample/student?tier=comprehensive`, '_blank')}
                      variant="outline"
                      className="flex-1 border-[#C5A065] text-[#C5A065] hover:bg-[#C5A065]/10"
                    >
                      Student (34pg)
                    </Button>
                  </div>
                </div>
              </div>

              {/* Demo Flow Instructions */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h4 className="text-white font-semibold mb-4">📋 Complete Demo Flow Instructions</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h5 className="text-[#CC5500] font-medium mb-2">For Professional Demo:</h5>
                    <ol className="list-decimal list-inside text-gray-400 space-y-1">
                      <li>Click "Start Professional Assessment" above</li>
                      <li>Complete the 65 scenario assessment</li>
                      <li>View your generated report</li>
                      <li>Test the Report Q&A feature</li>
                    </ol>
                  </div>
                  <div>
                    <h5 className="text-[#C5A065] font-medium mb-2">For Student Demo:</h5>
                    <ol className="list-decimal list-inside text-gray-400 space-y-1">
                      <li>Click "Create Demo Student" above</li>
                      <li>Copy the credentials shown in toast</li>
                      <li>Logout and login with demo credentials</li>
                      <li>Complete assessment and view report</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Report Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-auto m-4">
            <div className="sticky top-0 bg-[#1a1a1a] border-b border-white/10 p-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-white">{selectedReport.user_name}</h2>
                <p className="text-sm text-gray-400">{selectedReport.user_email}</p>
              </div>
              <div className="flex gap-2">
                {/* Language Selection Dropdown */}
                <select 
                  id="report-language"
                  className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 text-sm"
                  defaultValue="en"
                  data-testid="language-select"
                >
                  <optgroup label="Indian Languages">
                    <option value="en">English</option>
                    <option value="hi">Hindi (हिंदी)</option>
                    <option value="mr">Marathi (मराठी)</option>
                    <option value="ta">Tamil (தமிழ்)</option>
                    <option value="te">Telugu (తెలుగు)</option>
                    <option value="bn">Bengali (বাংলা)</option>
                    <option value="gu">Gujarati (ગુજરાતી)</option>
                  </optgroup>
                  <optgroup label="International">
                    <option value="es">Spanish (Español)</option>
                    <option value="fr">French (Français)</option>
                    <option value="de">German (Deutsch)</option>
                    <option value="ar">Arabic (العربية)</option>
                    <option value="zh">Chinese (中文)</option>
                    <option value="ja">Japanese (日本語)</option>
                  </optgroup>
                </select>
                <Button
                  variant="outline"
                  onClick={() => handleDownloadReport(selectedReport.id, 'pdf')}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                {selectedReport.status !== 'sent_to_user' && (
                  <Button
                    onClick={() => handleReleaseReport(selectedReport.id)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Release to User
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={() => setSelectedReport(null)}
                  className="text-gray-400 hover:text-white"
                >
                  Close
                </Button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Assessment Type</p>
                  <p className="text-white capitalize">{selectedReport.assessment_type}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-sm text-gray-500">User Type</p>
                  <p className="text-white capitalize">{selectedReport.user_type}</p>
                </div>
              </div>
              
              {/* Executive Summary */}
              {selectedReport.insights?.executive_summary && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-3">Executive Summary</h3>
                  <p className="text-gray-400 leading-relaxed">{selectedReport.insights.executive_summary}</p>
                </div>
              )}

              {/* Key Scores */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {selectedReport.insights?.leadership_evaluation_matrix && Object.entries(selectedReport.insights.leadership_evaluation_matrix).slice(0, 4).map(([key, value]) => (
                  <div key={key} className="bg-white/5 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-[#CC5500]">{typeof value === 'number' ? value : '-'}%</p>
                    <p className="text-sm text-gray-400 capitalize">{key.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
